import React, { useState } from 'react';
import { useSettings } from '../context/SettingsContext';

const FREQUENCIES = ['hourly', 'daily', 'weekdays', 'weekly'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS_12 = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
const MINUTES = ['00', '15', '30', '45'];

const TimePicker = ({ value, onChange }) => {
  const [h, m] = value ? value.split(':').map(Number) : [9, 0];
  const isPM = h >= 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const minStr = String(m).padStart(2, '0');

  const emit = (newH12, newMin, newIsPM) => {
    let h24 = newH12 % 12;
    if (newIsPM) h24 += 12;
    onChange(`${String(h24).padStart(2, '0')}:${newMin}`);
  };

  const sel = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white';
  return (
    <div className="flex gap-2">
      <select value={h12} onChange={e => emit(Number(e.target.value), minStr, isPM)} className={sel}>
        {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <select value={minStr} onChange={e => emit(h12, e.target.value, isPM)} className={sel}>
        {MINUTES.map(min => <option key={min} value={min}>:{min}</option>)}
      </select>
      <select value={isPM ? 'PM' : 'AM'} onChange={e => emit(h12, minStr, e.target.value === 'PM')} className={sel}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const RequestForm = ({ initial, onSave, onCancel }) => {
  const { settings } = useSettings();
  const [name, setName] = useState(initial?.name || '');
  const [prompt, setPrompt] = useState(initial?.prompt || '');
  const [frequency, setFrequency] = useState(initial?.frequency || 'daily');
  const [time, setTime] = useState(initial?.time || '09:00');
  const [dayOfWeek, setDayOfWeek] = useState(
    initial?.day_of_week != null ? initial.day_of_week : 1
  );
  const [emailNotify, setEmailNotify] = useState(
    initial ? Boolean(initial.email_notify) : false
  );
  const [emailAddress, setEmailAddress] = useState(initial?.email_address || settings.default_email || '');
  const [telegramNotify, setTelegramNotify] = useState(
    initial ? Boolean(initial.telegram_notify) : false
  );
  const [maskPrompt, setMaskPrompt] = useState(
    initial ? Boolean(initial.mask_prompt) : false
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!prompt.trim()) e.prompt = 'Prompt is required';
    if (frequency !== 'hourly' && !time) e.time = 'Time is required for this frequency';
    if (!emailNotify && !telegramNotify) e.notify = 'Select at least one notification channel';
    if (emailNotify && !emailAddress.trim()) e.emailAddress = 'Email address is required';
    else if (emailNotify && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress.trim())) e.emailAddress = 'Please enter a valid email address';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    await onSave({
      name: name.trim(),
      prompt: prompt.trim(),
      frequency,
      time: frequency === 'hourly' ? null : time,
      day_of_week: frequency === 'weekly' ? dayOfWeek : null,
      email_notify: emailNotify,
      email_address: emailNotify ? emailAddress.trim() : null,
      telegram_notify: telegramNotify,
      mask_prompt: maskPrompt,
    });
    setSubmitting(false);
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const pillBtn = (active) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${
    active ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="text-lg font-semibold mb-4">
          {initial ? 'Edit Request' : 'New Research Request'}
        </h3>

        <div className="mb-4">
          <label className={labelCls}>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g., AI News Digest" maxLength={255} className={inputCls} />
          {errors.name && <span className="text-red-500 text-xs mt-1 block">{errors.name}</span>}
        </div>

        <div className="mb-4">
          <label className={labelCls}>Prompt</label>
          <textarea rows={4} value={prompt} onChange={e => setPrompt(e.target.value)}
            placeholder="e.g., Summarize the latest news on AI advancements"
            maxLength={10000} className={inputCls + ' resize-vertical'} />
          {errors.prompt && <span className="text-red-500 text-xs mt-1 block">{errors.prompt}</span>}
        </div>

        <div className="flex gap-4 mb-4 flex-wrap">
          <div className="flex-1 min-w-[120px]">
            <label className={labelCls}>Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className={inputCls}>
              {FREQUENCIES.map(f => (
                <option key={f} value={f}>
                  {f === 'weekdays' ? 'Weekdays Only' : f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {frequency === 'weekly' && (
            <div className="flex-1 min-w-[120px]">
              <label className={labelCls}>Day</label>
              <select value={dayOfWeek} onChange={e => setDayOfWeek(parseInt(e.target.value))} className={inputCls}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          )}
        </div>

        {frequency !== 'hourly' && (
          <div className="mb-4">
            <label className={labelCls}>Time (UTC)</label>
            <TimePicker value={time} onChange={setTime} />
            {errors.time && <span className="text-red-500 text-xs mt-1 block">{errors.time}</span>}
          </div>
        )}

        {/* Notification channels */}
        <div className="mb-4 border-t pt-3">
          <label className={labelCls}>Notification Channel</label>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => setTelegramNotify(v => !v)} className={pillBtn(telegramNotify)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-2.04 9.613c-.15.678-.546.843-1.107.524l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.566-4.46c.537-.194 1.006.13.93.591z"/>
              </svg>
              Telegram
            </button>
            <button type="button" onClick={() => setEmailNotify(v => !v)} className={pillBtn(emailNotify)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Email
            </button>
          </div>
          {errors.notify && <span className="text-red-500 text-xs mt-1 block">{errors.notify}</span>}
          {emailNotify && (
            <div className="mt-3">
              <input type="email" value={emailAddress} onChange={e => setEmailAddress(e.target.value)}
                placeholder="Email address for notifications" className={inputCls} />
              {errors.emailAddress && <span className="text-red-500 text-xs mt-1 block">{errors.emailAddress}</span>}
            </div>
          )}
        </div>

        {/* Mask prompt */}
        <div className="mb-6">
          <button type="button" onClick={() => setMaskPrompt(v => !v)} className={pillBtn(maskPrompt)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            Mask prompt
          </button>
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={submitting}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-60 transition">
            {submitting ? 'Saving...' : initial ? 'Update' : 'Create'}
          </button>
          <button type="button" onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 transition">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequestForm;
